import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Alert,
  AssetCard,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CurrencyAmountUI,
  cn,
  DataTable,
  DateUI,
  Icon,
  LabelPill,
  PaymentIdentifier,
  PaymentIdentifierLabel,
  PrimaryFullScreenSpinner,
  StatusUI,
  toast,
  uppercaseStatusToStatus,
} from "@repo/ui";
import { formatCurrencyAmount, NotFoundError, neverFunc } from "@repo/utils";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { X } from "lucide-react";
import { Suspense, useState } from "react";
import { RiBuilding2Line } from "react-icons/ri";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type {
  StripePayoutQueueItemRowFragment,
  StripePayoutQueueItemStatus,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

const tabs = ["pending", "paid", "voided"] as const;

type Tab = (typeof tabs)[number];

const columns: ColumnDef<StripePayoutQueueItemRowFragment>[] = [
  {
    id: "date",
    header: "Arrival date",
    accessorKey: "arrivalDate",
    cell(c) {
      return <DateUI date={new Date(c.row.original.arrivalDate)} />;
    },
  },
  {
    id: "organisation",
    header: "Organisation",
    accessorFn(c) {
      return c.organisation?.name;
    },
    cell(c) {
      return (
        <div className="flex max-w-32 flex-row items-center gap-2 overflow-hidden">
          <Icon
            imageUrl={c.row.original.organisation?.squareLogoUrl ?? undefined}
            fallbackIcon={<RiBuilding2Line />}
            size="6"
          />
          <p className="truncate">{c.row.original.organisation?.name}</p>
        </div>
      );
    },
  },
  {
    id: "grossAmount",
    header: "Gross amount",
    accessorFn(c) {
      return formatCurrencyAmount({ ...c.grossAmount, notation: "standard" });
    },
    cell(c) {
      return <CurrencyAmountUI ca={c.row.original.grossAmount} nonCopyable />;
    },
  },
  {
    id: "feeAmount",
    header: "Fee amount",
    accessorFn(c) {
      return formatCurrencyAmount({ ...c.feeAmount, notation: "standard" });
    },
    cell(c) {
      return <CurrencyAmountUI ca={c.row.original.feeAmount} nonCopyable />;
    },
  },
  {
    id: "netAmount",
    header: () => <span className="font-semibold">Net amount</span>,
    accessorFn(c) {
      return formatCurrencyAmount({ ...c.netAmount, notation: "standard" });
    },
    cell(c) {
      return (
        <div className="flex flex-row items-center gap-3">
          <CurrencyAmountUI
            ca={c.row.original.netAmount}
            nonCopyable
            className="font-semibold"
          />
          <div className="invisible group-hover/data-table-row:visible">
            <Button
              size="xs"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const amountToCopy = new Intl.NumberFormat("en-AU", {
                  useGrouping: false, // Removes thousands separators
                  minimumFractionDigits: 2, // Keeps decimal precision
                  maximumFractionDigits: 2,
                }).format(c.row.original.netAmount.amount.toNumber());
                navigator.clipboard.writeText(amountToCopy);
                toast({
                  kind: "success",
                  title: `${amountToCopy} copied to clipboard`,
                  autoHide: true,
                });
              }}
            >
              Copy
            </Button>
          </div>
        </div>
      );
    },
  },
  {
    id: "transferTo",
    header: "Transfer to",
    accessorFn(c) {
      return c.transferToAsset?.friendlyIdentifier;
    },
    cell(c) {
      if (c.row.original.transferToAsset) {
        return (
          <div className="py-2">
            <AssetCard
              asset={c.row.original.transferToAsset}
              variant="IDENTIFIER"
            />
          </div>
        );
      }
      return <>-</>;
    },
  },
  {
    id: "stripePayoutStatus",
    header: "Stripe Payout Status",
    accessorFn(c) {
      return c.stripePayoutStatus;
    },
    cell(c) {
      return (
        <LabelPill
          variant={
            c.row.original.stripePayoutStatus === "PAID" ? "success" : "warning"
          }
          label={c.row.original.stripePayoutStatus ?? "UNKNOWN"}
        />
      );
    },
  },
];

export const Route = createFileRoute("/__auth/stripe-payout-queue/$tab")({
  head: () => opsHead("Stripe Payout Queue"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const params = useParams({ strict: false });

  let selectedTab: Tab | undefined;
  if (tabs.includes(params["tab"] as Tab)) {
    selectedTab = params["tab"] as Tab;
  } else {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(
    graphql(`
    query StripePayoutQueuePage {
        stripePayoutQueueItems {
            id
            ...StripePayoutQueueItemRow
        }
    }

    fragment StripePayoutQueueItemRow on StripePayoutQueueItem {
        id
        organisation {
            id
            name
            squareLogoUrl
        }
        transferToAsset {
            ...AssetCardFields
            paymentIdentifiers {
                ...PaymentIdentifierComponent
            }
            ...on FXAsset {
                eburyAccount {
                    id
                    clientId
                }
            }
        }
        grossAmount {
            currency
            amount
        }
        feeAmount {
            currency
            amount
        }
        netAmount {
            currency
            amount
        }
        status
        stripePayoutStatus
        arrivalDate
        stripeId
        createdAt
    }
  `),
  );

  const [updateStripePayoutQueueItemStatus, { loading: statusUpdating }] =
    useMutation(
      graphql(`
        mutation UpdateStripePayoutQueueItemStatus($input: UpdateStripePayoutQueueItemStatusInput!) {
          updateStripePayoutQueueItemStatus(input: $input) {
            stripePayoutQueueItem {
              id
              status
            }
          }
        }
      `),
    );

  const rows = (data.stripePayoutQueueItems ?? [])
    .filter((r) => {
      switch (selectedTab) {
        case "paid":
          return r.status === "PAID";
        case "pending":
          return r.status === "PENDING";
        case "voided":
          return r.status === "VOIDED";
        default:
          throw neverFunc(selectedTab);
      }
    })
    .sort((a, b) => {
      // force paid payouts above in_transit payouts
      return a.stripePayoutStatus > b.stripePayoutStatus ? -1 : 1;
    });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sidePaneVisible, setSidePaneVisible] = useState(false);
  const selectedItem = (data.stripePayoutQueueItems ?? []).find(
    (item) => item.id === selectedId,
  );
  const actionsEnabled = selectedItem?.status === "PENDING";

  const tabToBadgeNumber: Record<Tab, number> = {
    paid: (data.stripePayoutQueueItems ?? []).reduce<number>(
      (acc, curr) => (curr.status === "PAID" ? acc + 1 : acc),
      0,
    ),
    pending: (data.stripePayoutQueueItems ?? []).reduce<number>(
      (acc, curr) => (curr.status === "PENDING" ? acc + 1 : acc),
      0,
    ),
    voided: (data.stripePayoutQueueItems ?? []).reduce<number>(
      (acc, curr) => (curr.status === "VOIDED" ? acc + 1 : acc),
      0,
    ),
  };

  const handleStatusUpdate = async (newStatus: StripePayoutQueueItemStatus) => {
    if (!selectedItem) {
      return;
    }
    try {
      await updateStripePayoutQueueItemStatus({
        variables: {
          input: {
            id: selectedItem.id,
            newStatus,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: ["StripePayoutQueuePage"],
      });
      toast({
        kind: "success",
        title: `Stripe payout status updated to ${newStatus.toLowerCase()}`,
      });
    } catch {
      toast({
        kind: "error",
        title: "Stripe payout update failed",
      });
    }
  };

  return (
    <AdminPageContainer testId="stripe-payout-queue">
      <AdminPageHeader title="Stripe Payout Queue" />
      <div className="flex items-start gap-2">
        <div className="grid w-full flex-1 gap-6" style={{ zIndex: 1 }}>
          <div className="flex justify-start gap-4">
            <OpsTab
              label="Pending"
              badgeNumber={tabToBadgeNumber["pending"]}
              to="pending"
            />
            <OpsTab
              label="Paid"
              badgeNumber={tabToBadgeNumber["paid"]}
              to="paid"
            />
            <OpsTab
              label="Voided"
              badgeNumber={tabToBadgeNumber["voided"]}
              to="voided"
            />
          </div>

          <div className="flex w-full flex-col gap-6 rounded-xl border border-zinc-100 bg-white px-6 pt-6 pb-6 shadow-md">
            <div className="flex justify-between">
              <div className="font-semibold text-lg">Stripe payouts</div>
            </div>

            <DataTable
              key={selectedTab}
              data={rows}
              activeRowId={
                sidePaneVisible ? (selectedId ?? undefined) : undefined
              }
              hidePagination={true}
              pageSize={10_000}
              onRowClick={(row) => {
                setSelectedId(row.original.id);
                setSidePaneVisible(
                  sidePaneVisible ? row.original.id !== selectedId : true,
                );
              }}
              cellClassName="py-1 px-4"
              rowClassName="group/data-table-row"
              columns={columns}
            />
          </div>
        </div>

        <div
          className={cn(
            "absolute top-0 right-0 h-full w-1/2 transform overflow-auto border-l bg-white p-8 shadow-elevate-drop-shadow duration-300 ease-in-out",
            sidePaneVisible
              ? "translate-x-0"
              : "pointer-events-none translate-x-full",
          )}
          style={{
            zIndex: 1,
          }}
        >
          {selectedItem ? (
            <div className="flex h-full flex-col gap-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-tertiary text-xs uppercase tracking-wide">
                    Stripe payout
                  </div>
                  <div className="mt-2 font-mono text-lg">
                    {selectedItem.stripeId}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <StatusUI
                      status={uppercaseStatusToStatus[selectedItem.status]}
                    />
                    <span className="text-tertiary text-xs">
                      {selectedItem.id}
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => {
                    setSidePaneVisible(false);
                  }}
                >
                  <X className="size-4" />
                </Button>
              </div>

              <div>
                <div className="mb-3 font-semibold text-lg">Actions</div>
                <div className="flex flex-row items-start gap-6">
                  <Button
                    onClick={() => {
                      void handleStatusUpdate("PAID");
                    }}
                    disabled={!actionsEnabled || statusUpdating}
                    className="w-auto"
                  >
                    Mark as paid
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      void handleStatusUpdate("VOIDED");
                    }}
                    disabled={!actionsEnabled || statusUpdating}
                    className="w-auto"
                  >
                    Void item
                  </Button>
                  <div
                    className={cn(
                      actionsEnabled && "invisible",
                      "text-tertiary text-xs",
                    )}
                  >
                    Actions are only available for pending items.
                  </div>
                </div>
              </div>

              <div className="font-semibold text-lg">Payout summary</div>
              <div className="grid grid-cols-[264px_70%] gap-6">
                <div>
                  <DetailRow
                    label="Gross amount"
                    value={<CurrencyAmountUI ca={selectedItem.grossAmount} />}
                  />
                  <DetailRow
                    label="Fee amount"
                    value={<CurrencyAmountUI ca={selectedItem.feeAmount} />}
                  />
                  <DetailRow
                    label="Net amount"
                    value={<CurrencyAmountUI ca={selectedItem.netAmount} />}
                  />
                </div>
                <div>
                  <DetailRow
                    label="Organisation"
                    value={
                      selectedItem.organisation ? (
                        <Link
                          to={`/organisations/${selectedItem.organisation.id}`}
                          className="hover:underline"
                        >
                          <div className="flex max-w-32 flex-row items-center gap-2 overflow-hidden">
                            <Icon
                              imageUrl={
                                selectedItem.organisation?.squareLogoUrl ??
                                undefined
                              }
                              fallbackIcon={<RiBuilding2Line />}
                              size="6"
                            />
                            <p className="truncate">
                              {selectedItem.organisation?.name}
                            </p>
                          </div>
                        </Link>
                      ) : (
                        "-"
                      )
                    }
                  />
                  <DetailRow
                    label="Arrival date"
                    value={<DateUI date={new Date(selectedItem.arrivalDate)} />}
                  />
                  <DetailRow
                    label="Stripe ID"
                    value={
                      <div className="overflow-hidden">
                        <p className="truncate font-mono text-sm">
                          {selectedItem.stripeId}
                        </p>
                      </div>
                    }
                  />
                </div>
              </div>

              <div>
                <div className="mb-3 font-semibold text-lg">
                  Transfer destination
                </div>
                {selectedItem.transferToAsset ? (
                  <Link to={`/assets/${selectedItem.transferToAsset?.id}`}>
                    <AssetCard
                      asset={selectedItem.transferToAsset}
                      variant="IDENTIFIER"
                    />
                  </Link>
                ) : (
                  <div className="text-sm text-tertiary">
                    No destination asset on file.
                  </div>
                )}
              </div>

              <div>
                <div className="mb-3 font-semibold text-lg">
                  Payment identitifiers
                </div>
                <div className="flex flex-col gap-2">
                  {selectedItem.transferToAsset?.__typename === "FXAsset" &&
                    selectedItem.transferToAsset?.eburyAccount && (
                      <Alert
                        kind="warning"
                        title="Important"
                        description={
                          <span>
                            The Ebury Client ID{" "}
                            <span className="font-semibold">
                              {
                                selectedItem.transferToAsset?.eburyAccount
                                  .clientId
                              }
                            </span>{" "}
                            must be provided as the payment reference for all
                            transfers into this account
                          </span>
                        }
                      />
                    )}

                  {selectedItem.transferToAsset?.paymentIdentifiers?.map(
                    (paymentIdentifier) => (
                      <Card key={paymentIdentifier.id}>
                        <CardHeader>
                          <CardTitle>
                            <h3 className="text-xl">
                              <PaymentIdentifierLabel
                                paymentIdentifier={paymentIdentifier}
                              />
                            </h3>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <PaymentIdentifier
                            paymentIdentifier={paymentIdentifier}
                          />
                        </CardContent>
                      </Card>
                    ),
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-tertiary">
              Select a payout to see details.
            </div>
          )}
        </div>
        {sidePaneVisible && (
          <div
            className="absolute top-0 left-0 h-full w-full overflow-hidden"
            style={{
              zIndex: 0,
            }}
            onClick={() => {
              setSidePaneVisible(false);
            }}
          ></div>
        )}
      </div>
    </AdminPageContainer>
  );
}

function OpsTab({
  to,
  label,
  badgeNumber,
}: {
  to: Tab;
  label: React.ReactNode;
  badgeNumber?: number | null;
}) {
  return (
    <Link
      to={`/stripe-payout-queue/${to}`}
      activeProps={{ className: "active" }}
      className={cn(
        "group grid grid-flow-row place-items-start gap-2 whitespace-nowrap rounded-lg p-4 ring-offset-white ",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        "min-w-40 max-w-60",
        "disabled:pointer-events-none disabled:opacity-50",
        "font-normal text-secondary",
        "border border-transparent",
        "[&.active]:bg-elevation-sunken-1 [&.active]:font-medium [&.active]:text-primary [&.active]:shadow-elevate-drop-shadow",
        "hover:bg-zinc-50 hover:shadow-md",
        "cursor-pointer",
      )}
    >
      <span className="text-sm text-tertiary group-[&.active]:text-inherit">
        {label}
      </span>
      {badgeNumber !== undefined && (
        <span className="text-lg">{badgeNumber}</span>
      )}
    </Link>
  );
}
